import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import AuditTimeline from "@/modules/racket_lab_admin/AuditTimeline";
import ScanAuditTimeline from "@/modules/racket_lab_admin/ScanAuditTimeline";
import JSONInspector from "@/modules/racket_lab_admin/JSONInspector";
import QrTokenRevokeDialog from "@/modules/racket_lab_admin/QrTokenRevokeDialog";
import useAdminApi from "@/hooks/useAdminApi";
import useAdminMutation from "@/hooks/useAdminMutation";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";
import { TOKEN_KIND_LABEL_KEY, labelKeyFor } from "@/i18n/racketLabMaps";

const Field = ({ label, value, testId, mono = false }) => (
  <div data-testid={testId} className="flex flex-col gap-0.5">
    <span className="text-[10px] uppercase tracking-wide text-slate-500">{label}</span>
    <span className={`text-sm text-slate-800 break-all ${mono ? "font-mono text-xs" : ""}`}>{value ?? "—"}</span>
  </div>
);

/**
 * RLAdminQrTokenDetail — Sub-sprint C.
 *
 * Detail view + audit/scan_audit timelines + click-to-reveal raw token
 * + soft-revoke (no hard delete UI).
 */
const RLAdminQrTokenDetail = () => {
  const t = useRacketLabAdminT();
  const navigate = useNavigate();
  const { id } = useParams();
  const [revealed, setRevealed] = useState(false);
  const { data, loading, error, reload } = useAdminApi(
    `/admin/qr-tokens/${encodeURIComponent(id)}`,
    { skip: !id },
  );
  const { run, loading: mutating } = useAdminMutation();

  const onRevoke = async (reason) => {
    await run("patch", `/admin/qr-tokens/${encodeURIComponent(id)}/revoke`, { reason });
    reload();
  };

  return (
    <div data-testid="rl-admin-qr-token-detail-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />

      <button
        type="button"
        data-testid="rl-qr-detail-back"
        onClick={() => navigate("/admin/racket-lab/qr-tokens")}
        className="inline-flex w-fit items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> {t("qr_tokens.detail.back_label")}
      </button>

      <header>
        <h1 className="text-xl font-semibold text-slate-900">{t("qr_tokens.detail.title")}</h1>
        <p className="text-xs text-slate-500">{t("qr_tokens.detail.subtitle")}</p>
      </header>

      {loading && <div data-testid="rl-qr-detail-loading" className="rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-500">{t("qr_tokens.detail.loading")}</div>}
      {!loading && error && (
        <div data-testid="rl-qr-detail-error" className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          {error.status === 404
            ? t("qr_tokens.detail.not_found", { id: id?.slice(0, 12) || "" })
            : `${t("qr_tokens.detail.error_prefix")} ${error.status || ""}: ${error.detail || t("qr_tokens.detail.error_unknown")}`}
        </div>
      )}

      {!loading && !error && data && (
        <>
          <section data-testid="rl-qr-token-detail" className="grid grid-cols-1 gap-4 rounded-md border border-slate-200 bg-white p-4 md:grid-cols-2">
            <Field label={t("qr_tokens.detail.fields.id")} value={data.id} testId="rl-qr-field-id" mono />
            <Field label={t("qr_tokens.detail.fields.kind")} value={t(labelKeyFor(TOKEN_KIND_LABEL_KEY, data.kind))} testId="rl-qr-field-kind" />
            <Field label={t("qr_tokens.detail.fields.state")} value={data.revoked ? t("qr_tokens.detail.state_revoked") : t("qr_tokens.detail.state_active")} testId="rl-qr-field-state" />
            <Field label={t("qr_tokens.detail.fields.expires_at")} value={data.expires_at} testId="rl-qr-field-expires" />
            <Field label={t("qr_tokens.detail.fields.single_use")} value={String(data.single_use)} testId="rl-qr-field-single-use" />
            <Field label={t("qr_tokens.detail.fields.used_at")} value={data.used_at} testId="rl-qr-field-used-at" />
            <Field label={t("qr_tokens.detail.fields.organisation")} value={data.issued_for_organisation_id} testId="rl-qr-field-org" mono />
            <Field label={t("qr_tokens.detail.fields.entity")} value={`${data.entity_kind || "—"} · ${data.entity_id || "—"}`} testId="rl-qr-field-entity" mono />
            <Field label={t("qr_tokens.detail.fields.scope")} value={(data.scope || []).join(", ") || "—"} testId="rl-qr-field-scope" />
            <Field label={t("qr_tokens.detail.fields.revoke_reason")} value={data.revoke_reason} testId="rl-qr-field-revoke-reason" />
          </section>

          <section className="rounded-md border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-medium text-amber-900">{t("qr_tokens.detail.raw_section.title")}</p>
                <p className="text-[10px] text-amber-700">{t("qr_tokens.detail.raw_section.subtitle")}</p>
              </div>
              <button
                type="button"
                data-testid="rl-qr-token-reveal-once"
                onClick={() => setRevealed((v) => !v)}
                className="inline-flex items-center gap-1 rounded-md border border-amber-400 bg-white px-3 py-1 text-xs hover:bg-amber-100"
              >
                {revealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {revealed ? t("qr_tokens.detail.raw_section.hide") : t("qr_tokens.detail.raw_section.reveal")}
              </button>
            </div>
            {revealed && (
              <code data-testid="rl-qr-token-raw-value" className="mt-2 block break-all rounded bg-white px-2 py-1 font-mono text-[11px] text-amber-900">
                {data.token || "—"}
              </code>
            )}
          </section>

          {!data.revoked && (
            <div>
              <QrTokenRevokeDialog
                disabled={mutating}
                onConfirm={onRevoke}
                trigger={
                  <button
                    data-testid="rl-qr-token-revoke-btn"
                    className="inline-flex items-center gap-1 rounded-md bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700"
                    disabled={mutating}
                  >
                    {t("qr_tokens.detail.revoke_btn")}
                  </button>
                }
              />
            </div>
          )}

          <section data-testid="rl-qr-audit-section">
            <h2 className="mb-2 text-sm font-medium text-slate-700">{t("qr_tokens.detail.audit_timeline_title")}</h2>
            <AuditTimeline entries={data.audit || []} dataTestId="rl-qr-token-audit-timeline" />
          </section>

          <section data-testid="rl-qr-scan-audit-section">
            <h2 className="mb-2 text-sm font-medium text-slate-700">{t("qr_tokens.detail.scan_audit_timeline_title")}</h2>
            <ScanAuditTimeline entries={data.scan_audit || []} dataTestId="rl-qr-token-scan-timeline" />
          </section>

          <section data-testid="rl-qr-json-section">
            <h2 className="mb-2 text-sm font-medium text-slate-700">{t("qr_tokens.detail.raw_document_title")}</h2>
            <JSONInspector value={data} dataTestId="rl-qr-token-json" />
          </section>
        </>
      )}
    </div>
  );
};

export default RLAdminQrTokenDetail;
